import React, { useEffect, useState } from 'react';
import { Accessibility, Check, X, ExternalLink, AlertCircle } from 'lucide-react';
import api from '../lib/api';

/**
 * Surface venues where the Euan's Guide bulk ingester found multiple
 * possible matches in the public sitemap. The admin picks the correct
 * one in one click (or dismisses if none apply). Designed to live on the
 * Admin Overview alongside the AccessAble bulk-paste tile.
 */
export default function EuansAmbiguousReview() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null);
  const [picking, setPicking] = useState({}); // slug -> bool

  function load() {
    api.get('/admin/accessibility/euans-ambiguous')
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }
  useEffect(() => { if (open && items === null) load(); }, [open, items]);

  async function pick(slug, url) {
    setPicking((p) => ({ ...p, [slug]: true }));
    try {
      await api.post('/admin/accessibility/pick-euans', { slug, url });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save pick');
    } finally {
      setPicking((p) => ({ ...p, [slug]: false }));
    }
  }

  async function dismiss(slug) {
    if (!window.confirm("None of these are this venue? We'll clear the candidates so it stops appearing here. The venue will simply have no Euan's Guide link until someone adds one manually.")) return;
    setPicking((p) => ({ ...p, [slug]: true }));
    try {
      await api.post('/admin/accessibility/dismiss-euans-candidates', { slug });
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to dismiss');
    } finally {
      setPicking((p) => ({ ...p, [slug]: false }));
    }
  }

  const count = items?.length ?? 0;
  // Don't render the tile at all when there's nothing to review and the
  // panel isn't open — keeps the dashboard tidy.
  if (items !== null && count === 0 && !open) return null;

  return (
    <div className="w-full" data-testid="euans-ambiguous-tile">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="toggle-euans-ambiguous"
        className="w-full text-left block bg-admin border border-charcoal/10 px-5 py-4 rounded-xl font-semibold hover:border-charcoal/30 transition-colors"
      >
        <span className="inline-flex items-center gap-2">
          <AlertCircle size={16} className="text-amber-700" />
          {open
            ? 'Hide Euan\u2019s Guide review queue'
            : `Confirm Euan\u2019s Guide matches${count ? ` (${count})` : ''} \u2192`}
        </span>
        <span className="block text-xs text-muted mt-1 font-normal">
          Some venues had several possible matches on Euan&apos;s Guide. Pick the right one in one click, or dismiss if none apply.
        </span>
      </button>

      {open && (
        <div className="mt-3 bg-admin border border-charcoal/10 rounded-xl p-5 space-y-4" data-testid="euans-ambiguous-panel">
          {items === null && <div className="text-xs text-muted">Loading…</div>}
          {items && items.length === 0 && (
            <div className="text-sm text-muted text-center py-4">
              Nothing to review — all auto-matched venues had a clear single best candidate. Re-run the accessibility refresh later to recheck.
            </div>
          )}
          {items && items.map((venue) => (
            <div key={venue.slug} className="border border-charcoal/10 rounded-lg bg-white p-4" data-testid={`euans-review-${venue.slug}`}>
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <div className="font-semibold text-sm">{venue.name}</div>
                  <div className="text-xs text-muted">
                    <code className="text-[10px]">{venue.slug}</code>
                    {venue.town && <span> · {venue.town}</span>}
                    <span> · {venue.candidates.length} candidates</span>
                  </div>
                </div>
                <button
                  onClick={() => dismiss(venue.slug)}
                  disabled={picking[venue.slug]}
                  data-testid={`dismiss-${venue.slug}`}
                  className="text-[11px] text-muted hover:text-charcoal underline whitespace-nowrap disabled:opacity-50"
                >
                  None apply — dismiss
                </button>
              </div>
              <div className="space-y-2">
                {venue.candidates.map((c) => (
                  <div key={c.url} className="flex items-center gap-3 text-xs">
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-charcoal capitalize">{c.town.replace(/-/g, ' ')}</div>
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-forest hover:underline inline-flex items-center gap-1 break-all"
                      >
                        Preview on Euan&apos;s Guide <ExternalLink size={10} />
                      </a>
                    </div>
                    <button
                      onClick={() => pick(venue.slug, c.url)}
                      disabled={picking[venue.slug]}
                      data-testid={`pick-${venue.slug}-${c.town}`}
                      className="shrink-0 inline-flex items-center gap-1 text-[11px] px-3 py-1.5 rounded-md bg-forest text-white hover:bg-forest-deep disabled:opacity-50"
                    >
                      <Check size={11} /> This one
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
